import { Box, Toolbar, Skeleton, useTheme } from '@mui/material';
import { StyledContentContainer } from '../../Main/style';
import SearchToolbar from './SearchToolbar';
import Advertisement from './Advertisement';
import VideoContents from './VideoContents/VideoContents';
import useSelectorTyped from 'store/useSelectorTyped';
import NurseContents from './NurseRecord/NurseContents';
import PatientList from './AdminList/PatientList';
import PatientUpdate from './AdminList/PatientUpdate';
import ClassSectionManagement from './ClassSectionManagement/ClassSectionManagement';
import AssignmentCheck from './Assignment/AssignmentCheck';

interface Props {
  menuDrawerWidth: number;
  menuState: string;
}

const MENU_STATES = {
  ASSIGNMENT: '과제 제출 확인',
  PATIENT_LIST: '가상환자 리스트',
  PATIENT_UPDATE: '가상환자 수정',
  VIDEO_MANAGEMENT: '핵심간호술기영상 관리',
  NURSE_RECORDS: '간호기록 전체조회',
  CLASS_SECTIONS: '분반 생성 및 관리',
} as const;

const LoadingSkeleton = () => (
  <Box
    display="flex"
    mt={2.5}
    columnGap={2.5}
    overflow="hidden"
    height="calc(100% - 180px)"
  >
    {[0, 1].map(index => (
      <Box
        key={index}
        flex={1}
        display="flex"
        flexDirection="column"
        overflow="auto"
      >
        <Skeleton variant="rectangular" sx={{ flex: 1 }} />
      </Box>
    ))}
  </Box>
);

interface ContentRendererProps {
  menuState: string;
  patientInfo: any;
}

// patientInfo가 필요한 메뉴들
const PATIENT_INFO_REQUIRED_MENUS = [
  MENU_STATES.VIDEO_MANAGEMENT,
  MENU_STATES.NURSE_RECORDS,
] as const;

const ContentRenderer = ({ menuState, patientInfo }: ContentRendererProps) => {
  switch (menuState) {
    case MENU_STATES.PATIENT_LIST:
      return (
        <Box>
          <PatientList />
        </Box>
      );
    case MENU_STATES.PATIENT_UPDATE:
      return (
        <Box>
          <PatientUpdate />
        </Box>
      );
    case MENU_STATES.ASSIGNMENT:
      return (
        <Box>
          <AssignmentCheck patientInfo={patientInfo} />
        </Box>
      );
    case MENU_STATES.VIDEO_MANAGEMENT:
      return (
        <Box>
          <VideoContents patientInfo={patientInfo} />
        </Box>
      );
    case MENU_STATES.NURSE_RECORDS:
      return (
        <Box>
          <NurseContents patientInfo={patientInfo} />
        </Box>
      );
    case MENU_STATES.CLASS_SECTIONS:
      return (
        <Box>
          <ClassSectionManagement />
        </Box>
      );
    default:
      return null;
  }
};

const DisplayInformation = ({ menuDrawerWidth, menuState }: Props) => {
  const { palette } = useTheme();
  const patientInfo = useSelectorTyped(state => state.patient.patient);

  const containerWidth = {
    xs: `calc(100% ${menuDrawerWidth === 220 ? '- 220px' : ''})`,
    xl: 'calc(100% - 220px)',
  };

  // patientInfo가 필요한 메뉴인지 확인하는 함수
  const isPatientInfoRequired = (menuState: string) => {
    return PATIENT_INFO_REQUIRED_MENUS.includes(
      menuState as typeof PATIENT_INFO_REQUIRED_MENUS[number]
    );
  };

  // Loading 상태를 표시할지 결정하는 조건
  const shouldShowLoading =
    !patientInfo &&
    menuState !== MENU_STATES.PATIENT_LIST &&
    menuState !== MENU_STATES.PATIENT_UPDATE &&
    menuState !== MENU_STATES.CLASS_SECTIONS &&
    isPatientInfoRequired(menuState);

  return (
    <Box
      display="flex"
      component="main"
      flexDirection="column"
      width={containerWidth}
      sx={{ paddingRight: '0px' }}
    >
      <Toolbar sx={{ boxShadow: '-4px 4px 10px rgba(0, 0, 0, 0.04)' }}>
        <SearchToolbar />
        <Advertisement />
      </Toolbar>

      <StyledContentContainer
        style={{
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: palette.primary.light,
        }}
      >
        {shouldShowLoading ? (
          <LoadingSkeleton />
        ) : (
          <ContentRenderer menuState={menuState} patientInfo={patientInfo} />
        )}
      </StyledContentContainer>
    </Box>
  );
};

export default DisplayInformation;
